import {
  FormGroup,
  FormGroupFeedback,
  IFormGroupProps,
  Intent,
  ISelectOption,
  ISelectProps,
  Select,
} from '@pinpointhq/thumbtack';
import { FieldHelperProps, FieldInputProps, FieldMetaProps, useField } from 'formik';
import * as React from 'react';

interface ISelectField
  extends Omit<ISelectProps, 'activeItem' | 'intent' | 'buttonProps' | 'fill' | 'onItemSelect' | 'usePortal'> {
  /**
   * Field name
   */
  name: string;
  /**
   * Whether this should be 100% width
   */
  fill?: boolean;
  /**
   * Pass through props for the +FormGroup+ wrapper
   */
  formGroupProps?: Omit<IFormGroupProps, 'labelFor'>;
  /**
   * Require usePortal in an attempt to force developers using this component to
   * indicate whether the Select is being used inside another Overlay component
   *
   * Set to +false+ if used inside a Dialog / Drawer otherwise the Overlay will
   * steal focus and will likely prevent the Select from being searchable
   */
  usePortal: boolean;
  /**
   * Optional initial field value. This is used when the field name does not match the field name in the formik form
   */
  initialFieldValue?: string;
  /**
   * If the SelectField is clearable then the onChange handler may well send back +null+
   * @param value
   */
  onChange?(value: string): void;
}

export function SelectField<T extends string>({
  name,
  fill = true,
  formGroupProps,
  initialFieldValue,
  onChange,
  ...selectProps
}: ISelectField) {
  const { items } = selectProps;
  const [field, meta, helpers]: [FieldInputProps<T>, FieldMetaProps<T>, FieldHelperProps<T>] = useField<T>(name);
  const hasError = meta.touched && meta.error;
  const fieldValue = field.value || initialFieldValue;
  const activeItem = items.find((item) => fieldValue && item.value === fieldValue);

  const onItemSelect = (item: ISelectOption) => {
    const value = item ? item.value : null;
    if (onChange) onChange(value);
    helpers.setValue(value as T);
  };

  return (
    <FormGroup {...formGroupProps} labelFor={name}>
      <Select
        {...selectProps}
        activeItem={activeItem}
        onItemSelect={onItemSelect}
        fill={fill}
        buttonProps={{ large: true }}
        intent={hasError ? Intent.DANGER : Intent.NONE}
      />
      {hasError && <FormGroupFeedback intent={Intent.DANGER}>{meta.error}</FormGroupFeedback>}
      {field.value ? <input type="hidden" name={field.name} value={field.value} /> : <div />}
    </FormGroup>
  );
}

export default SelectField;
